// frontend/src/pages/OrgChart.js
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getHierarchy, getUsers, updateUserManager, updateUserRole, updateUser } from '../services/api';
import { Avatar, Badge, Btn, Modal, Field, Select, Spinner, Card, SectionTitle } from '../components/UI';
import PageHeader from '../components/PageHeader';

const ROLE_COLOR  = { admin: 'red', dept_head: 'green', manager: 'amber', employee: 'blue' };
const ROLE_LABELS = { admin: 'Admin', dept_head: 'Department Head', manager: 'Manager', employee: 'Employee' };

const PERMISSION_MATRIX = [
  { feature: 'View own profile & reviews',          admin: true,  manager: true,  employee: true  },
  { feature: 'Submit self review',                  admin: true,  manager: true,  employee: true  },
  { feature: 'Give peer feedback',                  admin: true,  manager: true,  employee: true  },
  { feature: 'View own OKRs & career plan',         admin: true,  manager: true,  employee: true  },
  { feature: 'Submit manager review for reports',   admin: true,  manager: true,  employee: false },
  { feature: 'View reports\' reviews & feedback',   admin: true,  manager: true,  employee: false },
  { feature: 'View reports\' OKRs & check-ins',     admin: true,  manager: true,  employee: false },
  { feature: 'Schedule 1:1 check-ins',              admin: true,  manager: true,  employee: false },
  { feature: 'View all team data',                  admin: true,  manager: false, employee: false },
  { feature: 'Manage users & roles',                admin: true,  manager: false, employee: false },
  { feature: 'Create review cycles',                admin: true,  manager: false, employee: false },
  { feature: 'Reassign reporting lines',            admin: true,  manager: false, employee: false },
  { feature: 'Delete records',                      admin: true,  manager: false, employee: false },
];

// ── Org node component ────────────────────────────────────────────────────
function OrgNode({ node, allUsers, onReassign, onRoleChange, depth = 0, isAdmin }) {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasReports = node.reports?.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Node card */}
      <div style={{ position: 'relative' }}>
        <div style={{
          background: 'var(--surface)', border: `0.5px solid ${depth === 0 ? 'var(--navy-500)' : 'var(--border)'}`,
          borderRadius: 10, padding: '12px 14px', width: 180, textAlign: 'center',
          boxShadow: depth === 0 ? '0 0 0 3px var(--navy-50)' : 'none',
          cursor: 'default', position: 'relative',
        }}>
          <Link to={`/profile/${node.id}`} style={{ textDecoration: 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
              <Avatar initials={node.avatar_initials} size={40} />
            </div>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-1)', marginBottom: 2 }}>{node.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 6 }}>{node.job_title || node.department || '—'}</div>
          </Link>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
            <Badge color={ROLE_COLOR[node.role]}>{ROLE_LABELS[node.role]}</Badge>
          </div>
          {isAdmin && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginTop: 8 }}>
              <button onClick={() => onRoleChange(node)}
                title="Change role"
                style={{ fontSize: 11, padding: '3px 8px', border: '0.5px solid var(--border)', borderRadius: 6, background: 'transparent', cursor: 'pointer', color: 'var(--text-2)', fontFamily: 'inherit' }}>
                Role
              </button>
              <button onClick={() => onReassign(node)}
                title="Reassign manager"
                style={{ fontSize: 11, padding: '3px 8px', border: '0.5px solid var(--border)', borderRadius: 6, background: 'transparent', cursor: 'pointer', color: 'var(--text-2)', fontFamily: 'inherit' }}>
                Move
              </button>
            </div>
          )}
        </div>

        {/* Expand toggle */}
        {hasReports && (
          <button onClick={() => setExpanded(e => !e)}
            style={{
              position: 'absolute', bottom: -12, left: '50%', transform: 'translateX(-50%)',
              width: 22, height: 22, borderRadius: '50%', border: '0.5px solid var(--border)',
              background: 'var(--surface)', cursor: 'pointer', fontSize: 11, display: 'flex',
              alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', zIndex: 1,
              fontFamily: 'inherit',
            }}>
            {expanded ? '−' : `+${node.reports.length}`}
          </button>
        )}
      </div>

      {/* Connector line down */}
      {hasReports && expanded && (
        <div style={{ width: 1, height: 24, background: 'var(--border-md)', marginTop: 12 }} />
      )}

      {/* Children row */}
      {hasReports && expanded && (
        <div style={{ position: 'relative', display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          {/* Horizontal connector */}
          {node.reports.length > 1 && (
            <div style={{
              position: 'absolute', top: 0, left: '50%',
              width: `calc(100% - ${180 + 16}px)`,
              transform: 'translateX(-50%)',
              height: 1, background: 'var(--border-md)',
            }} />
          )}
          {node.reports.map(child => (
            <div key={child.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ width: 1, height: 20, background: 'var(--border-md)' }} />
              <OrgNode
                node={child} allUsers={allUsers}
                onReassign={onReassign} onRoleChange={onRoleChange}
                depth={depth + 1} isAdmin={isAdmin}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────
export default function OrgChart() {
  const { user: me } = useAuth();
  const [tree,      setTree]      = useState([]);
  const [flatUsers, setFlatUsers] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState('chart');

  // Reassign modal
  const [reassignTarget, setReassignTarget] = useState(null);
  const [newManager,     setNewManager]     = useState('');
  const [saving,         setSaving]         = useState(false);

  // Role change modal
  const [roleTarget, setRoleTarget] = useState(null);
  const [newRole,    setNewRole]    = useState('');

  const load = useCallback(async () => {
    const [h, u] = await Promise.all([getHierarchy().catch(() => []), getUsers().catch(() => [])]);
    setTree(Array.isArray(h) ? h : []); setFlatUsers(Array.isArray(u) ? u : []);
  }, []);

  useEffect(() => { load().finally(() => setLoading(false)); }, [load]);

  const handleReassign = async () => {
    setSaving(true);
    try {
      await updateUserManager(reassignTarget.id, newManager || null);
      await load();
      setReassignTarget(null);
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  const handleRoleChange = async () => {
    setSaving(true);
    try {
      await updateUserRole(roleTarget.id, newRole);
      await load();
      setRoleTarget(null);
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  const isAdmin = me?.role === 'admin';

  // Flat list for the access management table view
  const roleGroups = {
    admin:    flatUsers.filter(u => u.role === 'admin'),
    manager:  flatUsers.filter(u => u.role === 'manager'),
    employee: flatUsers.filter(u => u.role === 'employee'),
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: '20px clamp(16px, 3vw, 32px)' }}>
      <div style={{ marginBottom: 24 }}>
        <PageHeader moduleKey="org-chart" />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '0.5px solid var(--border)', marginBottom: 28 }}>
        {['chart','access','team'].map(t => (
          <button key={t} onClick={() => setActiveTab(t)}
            style={{
              padding: '8px 18px', background: 'none', border: 'none', cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 13, fontWeight: activeTab === t ? 500 : 400,
              color: activeTab === t ? 'var(--navy-600)' : 'var(--text-3)',
              borderBottom: `2px solid ${activeTab === t ? 'var(--navy-600)' : 'transparent'}`,
              marginBottom: -1, textTransform: 'capitalize',
            }}>
            {t === 'chart' ? 'Org chart' : t === 'access' ? 'Access matrix' : 'Team roster'}
          </button>
        ))}
      </div>

      {/* ── ORG CHART ── */}
      {activeTab === 'chart' && (
        <div>
          {isAdmin && (
            <div style={{ background: 'var(--navy-50)', border: '0.5px solid var(--navy-100)', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: 'var(--navy-600)' }}>
              Click <strong>Role</strong> on any node to change their role, or <strong>Move</strong> to reassign their manager.
            </div>
          )}
          <div style={{ overflowX: 'auto', paddingBottom: 32 }}>
            <div style={{ display: 'flex', gap: 32, justifyContent: 'center', minWidth: 'max-content', paddingTop: 12 }}>
              {tree.map(root => (
                <OrgNode
                  key={root.id} node={root} allUsers={flatUsers}
                  onReassign={n => { setReassignTarget(n); setNewManager(String(n.manager_id || '')); }}
                  onRoleChange={n => { setRoleTarget(n); setNewRole(n.role); }}
                  depth={0} isAdmin={isAdmin}
                />
              ))}
            </div>
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', gap: 20, justifyContent: 'center', marginTop: 24, flexWrap: 'wrap' }}>
            {Object.entries(ROLE_COLOR).map(([role, color]) => (
              <div key={role} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Badge color={color}>{ROLE_LABELS[role]}</Badge>
                <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                  {role === 'admin' ? '— full access' : role === 'dept_head' ? '— department access' : role === 'manager' ? '— team access' : '— self access'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── ACCESS MATRIX ── */}
      {activeTab === 'access' && (
        <div>
          <div style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6 }}>
              Access is determined by role. Managers can access data for their full reporting subtree (direct and indirect reports). 
              Employees can only access their own data.
            </p>
          </div>
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)' }}>
                  <th style={{ padding: '12px 18px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-3)', borderBottom: '0.5px solid var(--border)', fontWeight: 500, width: '50%' }}>
                    Feature / Permission
                  </th>
                  {['admin','manager','employee'].map(r => (
                    <th key={r} style={{ padding: '12px 18px', textAlign: 'center', borderBottom: '0.5px solid var(--border)', fontWeight: 500 }}>
                      <Badge color={ROLE_COLOR[r]}>{ROLE_LABELS[r]}</Badge>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PERMISSION_MATRIX.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '0.5px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'var(--gray-50)' }}>
                    <td style={{ padding: '10px 18px', color: 'var(--text-2)' }}>{row.feature}</td>
                    {['admin','manager','employee'].map(r => (
                      <td key={r} style={{ padding: '10px 18px', textAlign: 'center' }}>
                        {row[r]
                          ? <span style={{ color: 'var(--navy-500)', fontSize: 16 }}>✓</span>
                          : <span style={{ color: 'var(--gray-200)', fontSize: 14 }}>—</span>
                        }
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {/* Role summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 12, marginTop: 24 }}>
            {Object.entries(roleGroups).map(([role, users]) => (
              <Card key={role}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <Badge color={ROLE_COLOR[role]}>{ROLE_LABELS[role]}</Badge>
                  <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{users.length} user{users.length !== 1 ? 's' : ''}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {users.map(u => (
                    <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Avatar initials={u.avatar_initials} size={24} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-3)' }}>{u.job_title || u.department || '—'}</div>
                      </div>
                      {isAdmin && (
                        <button onClick={() => { setRoleTarget(u); setNewRole(u.role); }}
                          style={{ marginLeft: 'auto', fontSize: 10, padding: '2px 7px', border: '0.5px solid var(--border)', borderRadius: 5, background: 'transparent', cursor: 'pointer', color: 'var(--text-3)', flexShrink: 0, fontFamily: 'inherit' }}>
                          Change
                        </button>
                      )}
                    </div>
                  ))}
                  {users.length === 0 && <p style={{ fontSize: 12, color: 'var(--text-3)' }}>No users</p>}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ── TEAM ROSTER ── */}
      {activeTab === 'team' && (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)' }}>
                {['Name','Role','Department','Job title','Reports to','Reports count'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-3)', borderBottom: '0.5px solid var(--border)', fontWeight: 500 }}>{h}</th>
                ))}
                {isAdmin && <th style={{ padding: '10px 16px', borderBottom: '0.5px solid var(--border)' }} />}
              </tr>
            </thead>
            <tbody>
              {flatUsers.map(u => {
                const reportCount = flatUsers.filter(x => x.manager_id === u.id).length;
                return (
                  <tr key={u.id} style={{ borderBottom: '0.5px solid var(--border)' }}>
                    <td style={{ padding: '11px 16px' }}>
                      <Link to={`/profile/${u.id}`} style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', color: 'inherit' }}>
                        <Avatar initials={u.avatar_initials} size={28} />
                        <div>
                          <div style={{ fontWeight: 500 }}>{u.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{u.email}</div>
                        </div>
                      </Link>
                    </td>
                    <td style={{ padding: '11px 16px' }}><Badge color={ROLE_COLOR[u.role]}>{u.role}</Badge></td>
                    <td style={{ padding: '11px 16px', color: 'var(--text-2)' }}>{u.department || '—'}</td>
                    <td style={{ padding: '11px 16px', color: 'var(--text-2)' }}>{u.job_title || '—'}</td>
                    <td style={{ padding: '11px 16px', color: 'var(--text-2)' }}>
                      {u.manager_name
                        ? <Link to={`/profile/${u.manager_id}`} style={{ color: 'var(--navy-600)', textDecoration: 'underline' }}>{u.manager_name}</Link>
                        : <span style={{ color: 'var(--text-3)' }}>—</span>}
                    </td>
                    <td style={{ padding: '11px 16px' }}>
                      {reportCount > 0
                        ? <span style={{ fontWeight: 500, color: 'var(--navy-600)' }}>{reportCount}</span>
                        : <span style={{ color: 'var(--text-3)' }}>0</span>}
                    </td>
                    {isAdmin && (
                      <td style={{ padding: '11px 16px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => { setRoleTarget(u); setNewRole(u.role); }}
                            style={{ fontSize: 11, padding: '4px 10px', border: '0.5px solid var(--border)', borderRadius: 6, background: 'transparent', cursor: 'pointer', color: 'var(--text-2)', fontFamily: 'inherit' }}>
                            Role
                          </button>
                          <button onClick={() => { setReassignTarget(u); setNewManager(String(u.manager_id || '')); }}
                            style={{ fontSize: 11, padding: '4px 10px', border: '0.5px solid var(--border)', borderRadius: 6, background: 'transparent', cursor: 'pointer', color: 'var(--text-2)', fontFamily: 'inherit' }}>
                            Manager
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* ── Reassign manager modal ── */}
      <Modal open={!!reassignTarget} onClose={() => setReassignTarget(null)} title={`Reassign manager — ${reassignTarget?.name}`} width={400}>
        {reassignTarget && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px', background: 'var(--gray-100)', borderRadius: 8, marginBottom: 16 }}>
              <Avatar initials={reassignTarget.avatar_initials} size={36} />
              <div>
                <div style={{ fontWeight: 500 }}>{reassignTarget.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                  Currently reports to: <strong>{flatUsers.find(u => u.id === reassignTarget.manager_id)?.name || 'No manager'}</strong>
                </div>
              </div>
            </div>
            <Field label="New manager">
              <Select value={newManager} onChange={e => setNewManager(e.target.value)}>
                <option value="">No manager (top-level)</option>
                {flatUsers
                  .filter(u => u.id !== reassignTarget.id)
                  .map(u => (
                    <option key={u.id} value={u.id}>{u.name} — {u.role}</option>
                  ))}
              </Select>
            </Field>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 16 }}>
              Note: circular reporting lines are blocked automatically.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn variant="secondary" onClick={() => setReassignTarget(null)}>Cancel</Btn>
              <Btn onClick={handleReassign} disabled={saving}>{saving ? 'Saving…' : 'Update reporting line'}</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Role change modal ── */}
      <Modal open={!!roleTarget} onClose={() => setRoleTarget(null)} title={`Change role — ${roleTarget?.name}`} width={400}>
        {roleTarget && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px', background: 'var(--gray-100)', borderRadius: 8, marginBottom: 16 }}>
              <Avatar initials={roleTarget.avatar_initials} size={36} />
              <div>
                <div style={{ fontWeight: 500 }}>{roleTarget.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  Current role: <Badge color={ROLE_COLOR[roleTarget.role]}>{ROLE_LABELS[roleTarget.role]}</Badge>
                </div>
              </div>
            </div>

            <Field label="New role">
              <Select value={newRole} onChange={e => setNewRole(e.target.value)}>
                <option value="employee">Employee — self access only</option>
                <option value="manager">Manager — team subtree access</option>
                <option value="admin">Admin — full access</option>
              </Select>
            </Field>

            {/* Role change impact */}
            <div style={{ background: newRole === 'admin' ? 'var(--red-50)' : newRole === 'manager' ? 'var(--amber-50)' : 'var(--navy-50)', borderRadius: 8, padding: '10px 12px', marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4, color: newRole === 'admin' ? 'var(--red-700)' : newRole === 'manager' ? 'var(--amber-700)' : 'var(--navy-600)' }}>
                {newRole === 'admin' && 'Full access to all team data and admin controls'}
                {newRole === 'manager' && 'Access to data for their direct and indirect reports'}
                {newRole === 'employee' && 'Self-access only — own reviews, OKRs, and profile'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                {newRole === 'manager' && 'Must have at least one direct report assigned to be effective.'}
                {newRole === 'employee' && 'They will lose visibility into any team they currently manage.'}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn variant="secondary" onClick={() => setRoleTarget(null)}>Cancel</Btn>
              <Btn
                variant={newRole === 'admin' ? 'danger' : 'primary'}
                onClick={handleRoleChange} disabled={saving || newRole === roleTarget.role}>
                {saving ? 'Saving…' : 'Update role'}
              </Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
